import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export const SIDEBAR_SECTION = {
    organization: 'organization.settings',
    sites: 'organization.sites',
    spaces: 'organization.spaces',
    settings: 'site.general.settings',
    customize: 'site.general.customize',
    extend: 'site.general.extend',
    improve: 'site.improve',
    analyze: 'site.analyze',
} as const;

export type SidebarSection = (typeof SIDEBAR_SECTION)[keyof typeof SIDEBAR_SECTION];
type SidebarSectionsUpdate =
    | Set<SidebarSection>
    | ((previous: Set<SidebarSection>) => Set<SidebarSection>);

export type IsSidebarSectionExpanded = (section: SidebarSection) => boolean;
export type SetSidebarSectionExpanded = (section: SidebarSection, isExpanded: boolean) => void;
export type SidebarSectionsExpanded = readonly [
    IsSidebarSectionExpanded,
    SetSidebarSectionExpanded,
];

const DEFAULT_EXPANDED_SIDEBAR_SECTIONS: SidebarSection[] = [
    SIDEBAR_SECTION.organization,
    SIDEBAR_SECTION.sites,
    SIDEBAR_SECTION.spaces,
    SIDEBAR_SECTION.improve,
    SIDEBAR_SECTION.analyze,
];
const ACCORDION_SIDEBAR_SECTIONS: readonly SidebarSection[] = [
    SIDEBAR_SECTION.settings,
    SIDEBAR_SECTION.customize,
    SIDEBAR_SECTION.extend,
];

const sidebarSectionsExpandedStorageAtom = atomWithStorage<SidebarSection[]>(
    'sidebarSectionsExpanded',
    DEFAULT_EXPANDED_SIDEBAR_SECTIONS,
    undefined,
    { getOnInit: true }
);

const sidebarSectionsExpandedAtom = atom(
    (get) => new Set(get(sidebarSectionsExpandedStorageAtom)),
    (get, set, update: SidebarSectionsUpdate) => {
        const previous = new Set(get(sidebarSectionsExpandedStorageAtom));
        const next = typeof update === 'function' ? update(previous) : update;
        if (next === previous) {
            return;
        }
        set(sidebarSectionsExpandedStorageAtom, Array.from(next));
    }
);

/** Manage the persisted expanded state of all sidebar sections. */
export function useSidebarSectionsExpanded(): SidebarSectionsExpanded {
    const [expandedSections, setExpandedSections] = useAtom(sidebarSectionsExpandedAtom);
    const isSidebarSectionExpanded: IsSidebarSectionExpanded = (section) =>
        expandedSections.has(section);
    const setSidebarSectionExpanded: SetSidebarSectionExpanded = (section, isExpanded) => {
        setExpandedSections((current) => {
            if (current.has(section) === isExpanded) {
                return current;
            }

            const next = new Set(current);
            if (isExpanded) {
                if (ACCORDION_SIDEBAR_SECTIONS.includes(section)) {
                    ACCORDION_SIDEBAR_SECTIONS.forEach((accordionSection) => {
                        next.delete(accordionSection);
                    });
                }
                next.add(section);
            } else {
                next.delete(section);
            }
            return next;
        });
    };

    return [isSidebarSectionExpanded, setSidebarSectionExpanded];
}
